import { matchRoutes, type RouteObject } from 'react-router-dom';

import { ENVIRONMENT_NAME } from '@/usage/config';
import { isMaskedRecording } from '@/usage/replayMask';
import { USAGE_EVENTS, cleanUsageProps } from '@/usage/events';

/**
 * The privacy filter of usage analytics. Everything here is pure: no SDK, no
 * session, no storage.
 *
 * Rules (owner 2026-10-12):
 * - an address is sent as its ROUTE PATTERN only (`/brandprofile/:id`), never
 *   with the ids of the page and never with its query string; a detail page's
 *   record goes separately as `record_type` + `record_id` (owner 2026-10-15:
 *   which records are used most), its numeric id only (usage/records.ts);
 * - an event carries only properties on the allow-list below;
 * - an event whose remaining values contain something that looks like an
 *   e-mail address or a token (JWT) is not sent at all;
 * - a replay chunk (`$snapshot`, only while replay is on) keeps its recording
 *   and the transport properties only, is sent only when every text and
 *   attribute value in it is masked (usage/replayMask.ts), and passes the
 *   same guard.
 */

// ---- addresses ----------------------------------------------------------------------

const PLAIN_WORD = /^[A-Za-z][A-Za-z_-]{0,39}$/;
const PATTERN_PART = /^(:[A-Za-z][A-Za-z0-9_]*|\*)$/;

/** The path of an address without its query string and fragment. */
const pathOnly = (value: string): string => {
  const path = String(value ?? '').split(/[?#]/)[0];
  return path.startsWith('/') ? path : `/${path}`;
};

/**
 * Fallback when no route pattern is known: every path segment that is not a
 * plain word (a number, a uuid, an e-mail, a token, anything with a digit or
 * an encoded character) becomes `:id`.
 */
export const maskPath = (value: string): string =>
  pathOnly(value)
    .split('/')
    .map((segment) =>
      segment === '' || PLAIN_WORD.test(segment) || PATTERN_PART.test(segment) ? segment : ':id',
    )
    .join('/');

/**
 * What an address no route knows is reported as: the catch-all route (`*`,
 * which sends the user home) under a name instead of `/*`. The address itself
 * is never sent: it may hold a name or an id.
 */
export const UNKNOWN_ROUTE = '/not-found';

export interface RouteMatch {
  /** the route pattern (`/brandprofile/:id`), `UNKNOWN_ROUTE` for the catch-all */
  pattern: string;
  /** the values of the route's parameters (`{ id: '123' }`); never sent as they are */
  params: Record<string, string | undefined>;
}

/**
 * The route of `pathname` as the router declares it (src/routes), or null
 * when no routes are known or matching fails.
 */
export const routeMatch = (
  routes: RouteObject[] | null | undefined,
  pathname: string,
): RouteMatch | null => {
  if (!routes || routes.length === 0) return null;
  try {
    const matches = matchRoutes(routes, { pathname: pathOnly(pathname) });
    if (!matches || matches.length === 0) return null;
    let pattern = '';
    for (const { route } of matches) {
      if (!route.path) continue;
      pattern = route.path.startsWith('/')
        ? route.path
        : `${pattern.replace(/\/+$/, '')}/${route.path}`;
    }
    if (!pattern) return null;
    return {
      pattern: pattern === '/*' ? UNKNOWN_ROUTE : pattern,
      params: { ...matches[matches.length - 1].params },
    };
  } catch {
    return null;
  }
};

/**
 * The route pattern of `pathname` as the router declares it (src/routes):
 * `/brandprofile/123` -> `/brandprofile/:id`; an address no route knows is
 * the catch-all, named `UNKNOWN_ROUTE`. Without routes, or when matching
 * fails: `maskPath`.
 */
export const routePattern = (routes: RouteObject[] | null | undefined, pathname: string): string =>
  routeMatch(routes, pathname)?.pattern ?? maskPath(pathname);

export type ToPattern = (pathname: string) => string;

const pageOrigin = (): string => (typeof window === 'undefined' ? '' : window.location.origin);

/** The address of the app's page with route pattern `pattern` (hash router), ids and query removed. */
export const patternUrl = (pattern: string): string =>
  typeof window === 'undefined'
    ? pattern
    : `${window.location.origin}${maskPath(window.location.pathname)}#${pattern}`;

export interface ScrubbedUrl {
  url: string;
  /** the route pattern, for an address of this app */
  route: string | null;
}

/**
 * An address in the form that may be sent:
 * - this app (same origin): origin + path + `#<route pattern>`, no query string;
 * - any other site: its origin only;
 * - not an http(s) address: null (the property is dropped).
 */
export const scrubUrl = (value: unknown, toPattern: ToPattern = maskPath): ScrubbedUrl | null => {
  if (typeof value !== 'string' || value === '') return null;
  let url: URL;
  try {
    url = new URL(value, pageOrigin() || undefined);
  } catch {
    return null;
  }
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
  if (url.origin !== pageOrigin()) return { url: url.origin, route: null };
  const base = `${url.origin}${maskPath(url.pathname)}`;
  if (!url.hash.startsWith('#/')) return { url: base, route: null };
  const route = toPattern(pathOnly(url.hash.slice(1)));
  return { url: `${base}#${route}`, route };
};

// ---- values that must never leave ------------------------------------------------------

const EMAIL_LIKE = /[A-Za-z0-9._+-]+(@|%40)[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}/i;
/** Three base64url parts, the first starting like an encoded JSON object. */
const JWT_LIKE = /eyJ[A-Za-z0-9_-]{6,}\.[A-Za-z0-9_-]{6,}\.[A-Za-z0-9_-]*/;

/** True when `value` (any depth) holds a string that looks like an e-mail address or a JWT. */
export const containsSensitive = (value: unknown, depth = 0): boolean => {
  if (typeof value === 'string') return EMAIL_LIKE.test(value) || JWT_LIKE.test(value);
  if (!value || typeof value !== 'object' || depth > 6) return false;
  return Object.entries(value as Record<string, unknown>).some(
    ([key, entry]) => containsSensitive(key, depth + 1) || containsSensitive(entry, depth + 1),
  );
};

// ---- the allow-list ---------------------------------------------------------------------

/** Events the SDK sends by itself that we keep; everything else it may emit is dropped. */
export const SYSTEM_EVENTS = ['$pageview', '$pageleave', '$identify', '$groupidentify'] as const;

const ALLOWED_EVENTS: ReadonlySet<string> = new Set<string>([...SYSTEM_EVENTS, ...USAGE_EVENTS]);

/** A chunk of a session replay, already masked by the recorder (usage/replay.ts). */
export const SNAPSHOT_EVENT = '$snapshot';

/** The recording itself, as the recorder hands it over. */
export const SNAPSHOT_PROPS = [
  '$snapshot_data',
  '$snapshot_bytes',
  '$snapshot_source',
  '$snapshot_host',
] as const;

/** What PostHog needs to accept and order an event, plain values only. */
export const TRANSPORT_PROPS = [
  'token',
  'distinct_id',
  '$user_id',
  '$session_id',
  '$window_id',
  '$pageview_id',
  '$prev_pageview_id',
  '$prev_pageview_duration',
  '$insert_id',
  '$time',
  '$lib',
  '$lib_version',
  '$is_identified',
  '$process_person_profile',
] as const;

/** Coarse device information (no user agent string, no language, no time zone). */
export const DEVICE_PROPS = [
  '$browser',
  '$os',
  '$device_type',
  '$viewport_width',
  '$viewport_height',
] as const;

/** Where, who and under which role, as a name / ids / enum values (set by usage/client.ts). */
export const CONTEXT_PROPS = ['environment', 'role_assignment_id', 'organisation_type'] as const;

export const GROUP_TYPE = 'organisation';

const INITIAL_PERSON_PROPS = [
  '$initial_current_url',
  '$initial_pathname',
  '$initial_referrer',
  '$initial_referring_domain',
] as const;

/**
 * A person or organisation id as usage/client.ts sends it: the numeric id,
 * outside production prefixed with the environment (`dev:812`).
 */
const USAGE_ID = /^([a-z][a-z0-9_-]{0,31}:)?[1-9]\d{0,17}$/;
const ENUM_VALUE = /^[A-Z][A-Z_]{0,31}$/;
const HOST_NAME = /^[A-Za-z0-9.-]{1,253}(:\d{1,5})?$/;
const DIRECT = '$direct';

const isPlain = (value: unknown): value is string | number | boolean =>
  typeof value === 'string' ||
  typeof value === 'boolean' ||
  (typeof value === 'number' && Number.isFinite(value));

const isPositiveInt = (value: unknown): value is number =>
  typeof value === 'number' && Number.isInteger(value) && value > 0;

type Props = Record<string, unknown>;

const asProps = (value: unknown): Props =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as Props) : {};

const referrer = (value: unknown, toPattern: ToPattern): string | null =>
  value === DIRECT ? DIRECT : (scrubUrl(value, toPattern)?.url ?? null);

const referringDomain = (value: unknown): string | null =>
  value === DIRECT || (typeof value === 'string' && HOST_NAME.test(value)) ? value : null;

const put = (target: Props, key: string, value: unknown): void => {
  if (value !== null && value !== undefined) target[key] = value;
};

/** The address properties of one bag (`prefix` = '$' or '$initial_'), in the form that may be sent. */
const addressProps = (source: Props, prefix: string, toPattern: ToPattern): Props => {
  const out: Props = {};
  const page = scrubUrl(source[`${prefix}current_url`], toPattern);
  put(out, `${prefix}current_url`, page?.url);
  const rawPath = source[`${prefix}pathname`];
  put(
    out,
    `${prefix}pathname`,
    page?.route ?? (typeof rawPath === 'string' && rawPath !== '' ? toPattern(rawPath) : null),
  );
  put(out, `${prefix}referrer`, referrer(source[`${prefix}referrer`], toPattern));
  put(out, `${prefix}referring_domain`, referringDomain(source[`${prefix}referring_domain`]));
  return out;
};

const eventProps = (source: Props, toPattern: ToPattern): Props => {
  const out: Props = {};
  for (const key of [...TRANSPORT_PROPS, ...DEVICE_PROPS]) {
    if (isPlain(source[key])) out[key] = source[key];
  }
  Object.assign(out, addressProps(source, '$', toPattern));

  if (typeof source.environment === 'string' && ENVIRONMENT_NAME.test(source.environment)) {
    out.environment = source.environment;
  }
  if (isPositiveInt(source.role_assignment_id)) out.role_assignment_id = source.role_assignment_id;
  if (typeof source.organisation_type === 'string' && ENUM_VALUE.test(source.organisation_type)) {
    out.organisation_type = source.organisation_type;
  }

  // the organisation the user acts for: its id only
  const organisation = asProps(source.$groups)[GROUP_TYPE];
  if (typeof organisation === 'string' && USAGE_ID.test(organisation)) {
    out.$groups = { [GROUP_TYPE]: organisation };
  }
  if (source.$group_type === GROUP_TYPE && typeof source.$group_key === 'string') {
    out.$group_type = GROUP_TYPE;
    out.$group_key = source.$group_key;
    const type = asProps(source.$group_set).organisation_type;
    out.$group_set =
      typeof type === 'string' && ENUM_VALUE.test(type) ? { organisation_type: type } : {};
  }

  return Object.assign(out, cleanUsageProps(source));
};

const personOnceProps = (source: Props, toPattern: ToPattern): Props => {
  const scrubbed = addressProps(source, '$initial_', toPattern);
  const out: Props = {};
  for (const key of INITIAL_PERSON_PROPS) put(out, key, scrubbed[key]);
  return out;
};

/** An event as the SDK hands it to `before_send`. */
export interface OutgoingEvent {
  event: string;
  properties?: Props;
  $set?: Props;
  $set_once?: Props;
  $unset?: string[];
  [key: string]: unknown;
}

const snapshotProps = (source: Props): Props => {
  const out: Props = {};
  for (const key of TRANSPORT_PROPS) {
    if (isPlain(source[key])) out[key] = source[key];
  }
  for (const key of SNAPSHOT_PROPS) {
    if (source[key] !== undefined) out[key] = source[key];
  }
  return out;
};

/**
 * The `before_send` filter: returns the event in the form that may be sent,
 * or null (the SDK then sends nothing). Replay chunks pass only with `replay`.
 */
export const createBeforeSend =
  (toPattern: ToPattern = maskPath, options: { replay?: boolean } = {}) =>
  (event: OutgoingEvent | null): OutgoingEvent | null => {
    if (!event || typeof event !== 'object') return null;
    const snapshot = event.event === SNAPSHOT_EVENT && options.replay === true;
    if (!snapshot && !ALLOWED_EVENTS.has(event.event)) return null;

    const source = asProps(event.properties);
    // only events of a signed-in user (numeric id, maybe with its environment); nothing anonymous is sent
    if (typeof source.distinct_id !== 'string' || !USAGE_ID.test(source.distinct_id)) return null;

    if (snapshot) {
      const chunk: OutgoingEvent = { ...event, properties: snapshotProps(source) };
      delete chunk.$set;
      delete chunk.$set_once;
      delete chunk.$unset;
      // second line behind the recorder's masking: every text and attribute must be masked
      const isMaskedUrl = (href: string) => scrubUrl(href, toPattern)?.url === href;
      if (!isMaskedRecording(chunk.properties!.$snapshot_data, isMaskedUrl)) return null;
      return containsSensitive(chunk.properties) ? null : chunk;
    }

    const properties = eventProps(source, toPattern);
    if (event.event === '$groupidentify') {
      if (typeof properties.$group_key !== 'string' || !USAGE_ID.test(properties.$group_key)) {
        return null;
      }
    }

    const clean: OutgoingEvent = { ...event, properties };
    delete clean.$unset;
    // no person property is set (no name, no e-mail); only the scrubbed first-visit addresses
    if ('$set' in event) clean.$set = {};
    if ('$set_once' in event)
      clean.$set_once = personOnceProps(asProps(event.$set_once), toPattern);

    if (containsSensitive([clean.properties, clean.$set, clean.$set_once])) return null;
    return clean;
  };
